<template>
  <div>
    <p class="text-title-small mb-2">More photos</p>
    <div class="d-flex flex-wrap ga-2">
      <div v-for="(photo, index) in photos" :key="photo.key" class="photo">
        <v-img
          :src="photo.src"
          :alt="`Photo ${index + 1}`"
          width="96"
          height="96"
          cover
          rounded
        />
        <v-btn
          icon="mdi-close"
          size="x-small"
          color="surface"
          class="remove"
          :aria-label="`Remove photo ${index + 1}`"
          @click="remove(photo)"
        />
      </div>
      <v-btn
        variant="tonal"
        height="96"
        width="96"
        prepend-icon="mdi-image-plus"
        stacked
        @click="($refs.input as HTMLInputElement).click()"
      >
        Add
      </v-btn>
    </div>
    <input
      ref="input"
      type="file"
      accept="image/*"
      multiple
      hidden
      aria-label="Add photos"
      @change="onFiles"
    />
  </div>
</template>

<script lang="ts">
  import {
    computed,
    defineComponent,
    onBeforeUnmount,
    type PropType,
  } from 'vue';

  interface Photo {
    key: string;
    src: string;
    url?: string;
    file?: File;
  }

  // Pictures beyond the cover: already saved ones (URLs) and newly picked
  // files, which are uploaded when the recipe is saved
  export default defineComponent({
    name: 'PhotoGallery',
    props: {
      existing: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      added: {
        type: Array as PropType<File[]>,
        default: () => [],
      },
    },
    emits: {
      'update:existing': (_urls: string[]) => true,
      'update:added': (_files: File[]) => true,
    },
    setup(props, { emit }) {
      const previews = new Map<File, string>();

      function preview(file: File): string {
        if (!previews.has(file)) {
          previews.set(file, URL.createObjectURL(file));
        }
        return previews.get(file)!;
      }

      onBeforeUnmount(() => {
        previews.forEach((src) => URL.revokeObjectURL(src));
      });

      const photos = computed<Photo[]>(() => [
        ...props.existing.map((url) => ({ key: url, src: url, url })),
        ...props.added.map((file, index) => ({
          key: `added-${index}-${file.name}`,
          src: preview(file),
          file,
        })),
      ]);

      return {
        photos,
        remove(photo: Photo) {
          if (photo.url) {
            emit(
              'update:existing',
              props.existing.filter((url) => url !== photo.url),
            );
          } else {
            emit(
              'update:added',
              props.added.filter((file) => file !== photo.file),
            );
          }
        },
        onFiles(event: Event) {
          const input = event.target as HTMLInputElement;
          const files = [...(input.files ?? [])];
          if (files.length) {
            emit('update:added', [...props.added, ...files]);
          }
          // Picking the same file again should add it again
          input.value = '';
        },
      };
    },
  });
</script>

<style scoped>
  .photo {
    position: relative;
  }

  .remove {
    position: absolute;
    top: 4px;
    right: 4px;
  }
</style>
